import { toURLSearchParams } from '@wix/metro-runtime';
import { serializer } from '@wix/metro-runtime/ambassador';
import { resolveUrl } from '@wix/metro-runtime';
var _applyBookingFeesToOrderRequest = {};
var _applyBookingFeesToOrderResponse = {};
var _collectAppliedBookingFeesRequest = {};
var _collectAppliedBookingFeesResponse = {};
var _listBookingFeesByBookingIdsRequest = {};
var _listBookingFeesByBookingIdsResponse = {};
var _listNonPreviewBookingFeesByBookingIdsRequest = {};
var _listNonPreviewBookingFeesByBookingIdsResponse = {};
function resolveWixBookingsFeesV1BookingFeesUrl(opts) {
    var domainToMappings = {
        'manage._base_domain_': [
            {
                srcPath: '/_api/booking-fees',
                destPath: '',
            },
        ],
        _: [
            {
                srcPath: '/_api/booking-fees',
                destPath: '',
            },
        ],
        'www.wixapis.com': [
            {
                srcPath: '/_api/booking-fees',
                destPath: '',
            },
        ],
        '*.dev.wix-code.com': [
            {
                srcPath: '/_api/booking-fees',
                destPath: '',
            },
        ],
    };
    return resolveUrl(Object.assign(opts, { domainToMappings: domainToMappings }));
}
/**
 * Retrieves booking fees by booking IDs and multi service booking IDs.
 *
 *
 * Instead of returning `bookingFee` objects with the `cancellationFee.price.value`
 * set to `0.00`, the endpoint doesn't return a `bookingFee` object for the relevant
 * booking IDs. For example, no `bookingFee` object is returned if the canceled
 * booking was free or if the booking was canceled before the start of the earliest
 * cancellation window.
 *
 * If the service's booking policy has been updated since the booking was created,
 * booking fees are calculated according to the
 * [policy snapshot](https://dev.wix.com/docs/rest/business-solutions/bookings/services/booking-policy-snapshots/introduction)
 * rather than the current version of the policy.
 *
 * This endpoint calculates the cancellation fee amount based on the time of the
 * call, you can't specify a time. Similarly, it calculates the cancellation fee
 * based on the number of participants who canceled, not a provided number.
 *
 * A `cancellationFee.status` of `PREVIEW` indicates that the booking fee is
 * informational only; the customer isn't required to pay it. When the `status` is
 * set to `UNKNOWN_STATUS` there is no eCommerce order associated with the booking. For
 * example, if a custom checkout was used for the booking instead of the
 * [Wix eCommerce checkout](https://dev.wix.com/docs/rest/business-solutions/e-commerce/checkout/introduction).
 *
 * If multiple events would trigger the calculation of a booking fee, for example
 * when a booking is first canceled and then marked as not attended, Wix calculates
 * the booking fee based on the first trigger. In this example, the booking
 * cancellation.
 */
export function listBookingFeesByBookingIds(payload) {
    var _a = serializer(_listBookingFeesByBookingIdsRequest, {}), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_listBookingFeesByBookingIdsResponse, {}).fromJSON;
    function __listBookingFeesByBookingIds(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.fees.v1.booking_fee',
            method: 'GET',
            methodFqn: 'wix.bookings.fees.v1.BookingFees.ListBookingFeesByBookingIds',
            url: resolveWixBookingsFeesV1BookingFeesUrl({
                protoPath: '/v1/booking-fees',
                data: serializedData,
                host: host,
            }),
            params: toURLSearchParams(serializedData),
            transformResponse: fromRes,
        };
        return metadata;
    }
    __listBookingFeesByBookingIds.fromReq = fromReq;
    __listBookingFeesByBookingIds.__isAmbassador = true;
    return __listBookingFeesByBookingIds;
}
/**
 * Applies booking fees to an
 * [eCommerce order](https://dev.wix.com/docs/rest/business-solutions/e-commerce/orders/introduction).
 *
 *
 * The booking fees are added as a single additional fee to the eCommerce order.
 * The order's `additionalFee.lineItemIds` array is set to the list of corresponding
 * booking IDs. By default, the `additionalFee.price.amount` is the sum of all
 * booking fee prices. But you may provide a `priceOverride` instead. The override
 * price can't be higher than the sum of all booking fees.
 *
 * Apply Booking Fees to Order also updates the prices of all affected line items
 * in the relevant eCommerce order to zero. After a cancellation fee is applied
 * to an eCommerce order, the cancellation fee's `price.value` is updated to `0.00`
 * and its trigger is set to `UNKNOWN_TRIGGER`. You can retrieve the fee amount
 * from the corresponding `additionalFee` object of the eCommerce order with
 * [Search Orders](https://dev.wix.com/docs/rest/business-solutions/e-commerce/orders/search-orders).
 *
 * If you apply multiple booking fees to an eCommerce order, they either all fail or
 * all succeed together. For example, the call fails if the booking fees are associated
 * with different eCommmerce orders.
 */
export function applyBookingFeesToOrder(payload) {
    var _a = serializer(_applyBookingFeesToOrderRequest, {}), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_applyBookingFeesToOrderResponse, {}).fromJSON;
    function __applyBookingFeesToOrder(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.fees.v1.booking_fee',
            method: 'POST',
            methodFqn: 'wix.bookings.fees.v1.BookingFees.ApplyBookingFeesToOrder',
            url: resolveWixBookingsFeesV1BookingFeesUrl({
                protoPath: '/v1/booking-fees/apply',
                data: serializedData,
                host: host,
            }),
            data: serializedData,
            transformResponse: fromRes,
        };
        return metadata;
    }
    __applyBookingFeesToOrder.fromReq = fromReq;
    __applyBookingFeesToOrder.__isAmbassador = true;
    return __applyBookingFeesToOrder;
}
/**
 * Retrieves non-preview booking fees by booking IDs.
 *
 *
 * Returns only booking fees with status `APPLIED_TO_ORDER` or `NOT_YET_APPLIED_TO_ORDER`.
 *
 * Use [List Booking Fees By Booking Ids](https://dev.wix.com/docs/rest/business-solutions/bookings/services/booking-policy/introduction)
 * to retrieve all booking fees.
 *
 * For performance reasons when retrieving only booking fees with status
 * `APPLIED_TO_ORDER` or `NOT_YET_APPLIED_TO_ORDER`, we recommend using this endpoint
 * over List Booking Fees By Booking Ids.
 */
export function listNonPreviewBookingFeesByBookingIds(payload) {
    var _a = serializer(_listNonPreviewBookingFeesByBookingIdsRequest, {}), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_listNonPreviewBookingFeesByBookingIdsResponse, {}).fromJSON;
    function __listNonPreviewBookingFeesByBookingIds(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.fees.v1.booking_fee',
            method: 'GET',
            methodFqn: 'wix.bookings.fees.v1.BookingFees.ListNonPreviewBookingFeesByBookingIds',
            url: resolveWixBookingsFeesV1BookingFeesUrl({
                protoPath: '/v1/non-preview-booking-fees',
                data: serializedData,
                host: host,
            }),
            params: toURLSearchParams(serializedData),
            transformResponse: fromRes,
        };
        return metadata;
    }
    __listNonPreviewBookingFeesByBookingIds.fromReq = fromReq;
    __listNonPreviewBookingFeesByBookingIds.__isAmbassador = true;
    return __listNonPreviewBookingFeesByBookingIds;
}
/**
 * Collects booking fees by charging the customer using the payment method that's
 * saved on the corresponding [eCommerce order](https://dev.wix.com/docs/rest/business-solutions/e-commerce/orders/introduction).
 *
 *
 * <blockquote class="warning">
 *
 * __Warning:__
 * Currently, there is no validation that prevents idempotent requests.
 * This means that your code must make sure to not charge customers multiple
 * times for the same booking fee. You could use
 * [List Transactions For Single Order](https://dev.wix.com/docs/rest/business-solutions/e-commerce/order-transactions/list-transactions-for-single-order)
 * to check which charges were made previously for an eCommerce order.
 *
 * </blockquote>
 *
 * An HTTP status of `200` means that all booking fees were successfully collected.
 * Any other HTPP status means that collection failed.
 *
 * Collects the order's `additionalFees.price.amount` that's related to the
 * booking fees. If there are multiple additional fees on the eCommerce order,
 * the amount that's colletced differs from `priceSummary.totalAdditionalFees.amount`.
 *
 * Possible failure reason's include:
 * + The order's `status` isn't `APPROVED`.
 * + There is no payment method saved on the order.
 * + The order's `balanceSummary.balance.amount` is lower than the
 * `additionalFees.price.amount` to collect.
 * + The order's `additionalFeeId` doesn't belong to a Wix Bookings cancellation fee.
 */
export function collectAppliedBookingFees(payload) {
    var _a = serializer(_collectAppliedBookingFeesRequest, {}), toReq = _a.toJSON, fromReq = _a.fromJSON;
    var fromRes = serializer(_collectAppliedBookingFeesResponse, {}).fromJSON;
    function __collectAppliedBookingFees(_a) {
        var host = _a.host;
        var serializedData = toReq(payload);
        var metadata = {
            entityFqdn: 'wix.bookings.fees.v1.booking_fee',
            method: 'POST',
            methodFqn: 'wix.bookings.fees.v1.BookingFees.CollectAppliedBookingFees',
            url: resolveWixBookingsFeesV1BookingFeesUrl({
                protoPath: '/v1/booking-fees/collect',
                data: serializedData,
                host: host,
            }),
            data: serializedData,
            transformResponse: fromRes,
        };
        return metadata;
    }
    __collectAppliedBookingFees.fromReq = fromReq;
    __collectAppliedBookingFees.__isAmbassador = true;
    return __collectAppliedBookingFees;
}
//# sourceMappingURL=http.impl.js.map